import type { MapGeometry } from "../shared/types";
import { mapPreviewOpacity } from "../shared/mapPresentation";
import earcut, { flatten } from "earcut";
import {
  VENUE_MAP_ARCHITECTURE_COLOR,
  VENUE_MAP_ARCHITECTURE_HEIGHT,
} from "./venueMapArchitecture";
import {
  mapEntranceDotPoints,
  VENUE_MAP_ENTRANCE_DOT_COLOR,
  VENUE_MAP_ENTRANCE_DOT_HEIGHT,
} from "./venueMapEntranceDot";
import {
  mapPolygonBoundary,
  mapPolygonBoundaryIndices,
  mapPolygonCornerIndices,
} from "./venueMapPolygon";
import {
  mapStandColors,
  mapStandBorderColor,
  mapStandRoofTint,
  venueMapPalette,
} from "./venueMapAppearance";
import { VENUE_MAP_ARTWORK_HEIGHT } from "./venueMapArtwork";
import { VENUE_MAP_RIM_TINT } from "./venueMapLighting";
import {
  mapPerspectiveDistance,
  mapStandHeight,
  type VenueMapCamera,
} from "./venueMapProjection";

export interface VenueMapWebGLViewport {
  x: number;
  y: number;
  width: number;
  height: number;
}

export type VenueMapWebGLPoint = { x: number; y: number };
type Color = readonly [number, number, number, number];
type Vertex = VenueMapWebGLPoint & { z: number };

export const venueMapWebGLPalette = venueMapPalette;

/** Closed screen-width roof ribbons, rebuilt only when selection changes. */
export function buildVenueMapWebGLSelection(
  geometry: MapGeometry,
  selectedStandId?: string,
): Float32Array {
  const stand = geometry.stands.find(
    (candidate) => candidate.id === selectedStandId && !candidate.hidden,
  );
  if (!stand) return new Float32Array();
  const vertices: number[] = [];
  // Above the roof edge, below lettering; depth testing still hides the
  // selection behind nearby taller booths, including in low-angle views.
  const z = Math.max(0.025, mapStandHeight(stand)) + 0.04;
  for (const source of stand.polygons) {
    const points = mapPolygonBoundary(source.map(([x, y]) => ({ x, y })));
    if (points.length < 3) continue;
    const vertex = (index: number, offset: number) => {
      const point = points[index % points.length];
      const previous = points[(index + points.length - 1) % points.length];
      const next = points[(index + 1) % points.length];
      vertices.push(
        point.x,
        point.y,
        z,
        previous.x,
        previous.y,
        z,
        next.x,
        next.y,
        z,
        0,
        offset,
      );
    };
    for (let index = 0; index < points.length; index++) {
      vertex(index, -1);
      vertex(index, 1);
      vertex(index + 1, -1);
      vertex(index + 1, -1);
      vertex(index, 1);
      vertex(index + 1, 1);
    }
  }
  return new Float32Array(vertices);
}

/** Bound memory by visible pixels, never the zoomed map's dimensions. */
export function venueMapWebGLBufferSize(
  width: number,
  height: number,
  dpr = 1,
) {
  const safeWidth = Number.isFinite(width) ? Math.max(1, width) : 1;
  const safeHeight = Number.isFinite(height) ? Math.max(1, height) : 1;
  const ratio = Number.isFinite(dpr) ? Math.min(2, Math.max(1, dpr)) : 1;
  const scale = Math.min(
    ratio,
    4096 / safeWidth,
    4096 / safeHeight,
    Math.sqrt(2_097_152 / safeWidth / safeHeight),
  );
  return {
    width: Math.max(1, Math.floor(safeWidth * scale)),
    height: Math.max(1, Math.floor(safeHeight * scale)),
    scale,
  };
}

/** Homogeneous eye in source units; w=0 is the orthographic viewing direction. */
export function venueMapWebGLViewPosition(
  camera: VenueMapCamera,
  geometry: Pick<MapGeometry, "width" | "height">,
): readonly [number, number, number, number] {
  const yaw = (camera.rotation * Math.PI) / 180;
  const pitch = (camera.pitch * Math.PI) / 180;
  const c = Math.cos(yaw),
    s = Math.sin(yaw);
  const cp = Math.cos(pitch),
    sp = Math.sin(pitch);
  const sx = camera.imageWidth / geometry.width;
  const sy = camera.imageHeight / geometry.height;
  const distance = mapPerspectiveDistance(camera);
  if (!Number.isFinite(distance))
    return [(s * sp) / sx, (c * sp) / sy, cp / sx, 0];
  const east = -(camera.panX || 0);
  const north = distance * sp - (camera.panY || 0) * cp;
  return [
    geometry.width / 2 + (east * c + north * s) / sx,
    geometry.height / 2 + (-east * s + north * c) / sy,
    (distance * cp + (camera.panY || 0) * sp) / sx,
    1,
  ];
}

/** Column-major source-SVG-to-clip matrix, matching projectMapPoint exactly. */
export function venueMapWebGLProjection(
  camera: VenueMapCamera,
  viewport: VenueMapWebGLViewport,
  geometry: Pick<MapGeometry, "width" | "height">,
): Float32Array {
  const yaw = (camera.rotation * Math.PI) / 180;
  const pitch = (camera.pitch * Math.PI) / 180;
  const c = Math.cos(yaw);
  const s = Math.sin(yaw);
  const cp = Math.cos(pitch);
  const sp = Math.sin(pitch);
  const sx = camera.imageWidth / geometry.width;
  const sy = camera.imageHeight / geometry.height;
  const rotatedOrigin = (-camera.imageWidth * s - camera.imageHeight * c) / 2;
  const x = [
    sx * c,
    -sy * s,
    0,
    (-camera.imageWidth * c + camera.imageHeight * s) / 2 + (camera.panX || 0),
  ];
  const y = [
    sx * s * cp,
    sy * c * cp,
    -sx * sp,
    rotatedOrigin * cp + (camera.panY || 0),
  ];
  const depth = [sx * s * sp, sy * c * sp, sx * cp, rotatedOrigin * sp];
  const inverseDistance = 1 / mapPerspectiveDistance(camera);
  const w = depth.map(
    (value, index) => (index === 3 ? 1 : 0) - value * inverseDistance,
  );
  const width = Math.max(1, viewport.width);
  const height = Math.max(1, viewport.height);
  const centerX =
    (2 * (camera.stageWidth / 2 + (camera.offsetX || 0) - viewport.x)) / width -
    1;
  const centerY =
    1 -
    (2 * (camera.stageHeight / 2 + (camera.offsetY || 0) - viewport.y)) /
      height;
  const matrix = new Float32Array(16);
  for (let column = 0; column < 4; column++) {
    matrix[column * 4] = centerX * w[column] + (2 * x[column]) / width;
    matrix[column * 4 + 1] = centerY * w[column] - (2 * y[column]) / height;
    // The same monotonically increasing eye depth as the SVG scene, with
    // enough range for the entire map at every supported pitch and rotation.
    matrix[column * 4 + 2] =
      -depth[column] /
      Math.max(
        1,
        camera.imageWidth * 4,
        camera.imageHeight * 4,
        ((camera.sourceElevation || 0) * camera.imageWidth * 4) /
          camera.sourceWidth,
      );
    matrix[column * 4 + 3] = w[column];
  }
  return matrix;
}

function area(points: readonly VenueMapWebGLPoint[]) {
  return points.reduce((sum, a, index) => {
    const b = points[(index + 1) % points.length];
    return sum + a.x * b.y - b.x * a.y;
  }, 0);
}

function cross(
  a: VenueMapWebGLPoint,
  b: VenueMapWebGLPoint,
  c: VenueMapWebGLPoint,
) {
  return (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
}

/** Bounded ear clipping retains the inset of concave booths. No fan fallback. */
export function triangulateVenueMapPolygon(
  points: readonly VenueMapWebGLPoint[],
): number[] {
  if (points.length < 3) return [];
  if (
    points.some(
      (point) => !Number.isFinite(point.x) || !Number.isFinite(point.y),
    )
  ) {
    throw new Error("Map polygon has invalid coordinates");
  }
  const remaining = mapPolygonBoundaryIndices(points);
  if (area(points) < 0) remaining.reverse();
  const triangles: number[] = [];
  let budget = points.length * points.length;
  while (remaining.length > 3 && budget-- > 0) {
    let clipped = false;
    for (let index = 0; index < remaining.length; index++) {
      const a = remaining[(index + remaining.length - 1) % remaining.length];
      const b = remaining[index];
      const c = remaining[(index + 1) % remaining.length];
      if (cross(points[a], points[b], points[c]) <= 1e-8) continue;
      const contains = remaining.some(
        (candidate) =>
          candidate !== a &&
          candidate !== b &&
          candidate !== c &&
          cross(points[a], points[b], points[candidate]) >= -1e-8 &&
          cross(points[b], points[c], points[candidate]) >= -1e-8 &&
          cross(points[c], points[a], points[candidate]) >= -1e-8,
      );
      if (contains) continue;
      triangles.push(a, b, c);
      remaining.splice(index, 1);
      clipped = true;
      break;
    }
    if (!clipped) throw new Error("Map polygon cannot be triangulated");
  }
  if (
    remaining.length === 3 &&
    Math.abs(
      cross(points[remaining[0]], points[remaining[1]], points[remaining[2]]),
    ) > 1e-8
  ) {
    triangles.push(...remaining);
  }
  return triangles;
}

export function venueMapWebGLColor(hex: string, alpha = 1): Color {
  return [1, 3, 5]
    .map((offset) => parseInt(hex.slice(offset, offset + 2), 16) / 255)
    .concat(alpha) as unknown as Color;
}

function vertex(
  target: number[],
  point: Vertex,
  normal: readonly number[],
  color: Color,
) {
  target.push(point.x, point.y, point.z, ...normal, ...color);
}

function surface(
  target: number[],
  points: readonly VenueMapWebGLPoint[],
  z: number,
  color: Color | ((point: VenueMapWebGLPoint) => Color),
) {
  for (const index of triangulateVenueMapPolygon(points)) {
    vertex(
      target,
      { ...points[index], z },
      [0, 0, 1],
      typeof color === "function" ? color(points[index]) : color,
    );
  }
}

function walls(
  target: number[],
  points: readonly VenueMapWebGLPoint[],
  bottom: number,
  top: number,
  color: Color,
) {
  const winding = Math.sign(area(points));
  points.forEach((point, index) => {
    const next = points[(index + 1) % points.length];
    const length = Math.hypot(next.x - point.x, next.y - point.y);
    if (!length) return;
    const normal = [
      (winding * (next.y - point.y)) / length,
      (-winding * (next.x - point.x)) / length,
      0,
    ];
    const corners = [
      { ...point, z: bottom },
      { ...next, z: bottom },
      { ...next, z: top },
      { ...point, z: top },
    ];
    for (const corner of [0, 1, 2, 0, 2, 3])
      vertex(target, corners[corner], normal, color);
  });
}

/** Corner ink belongs to the solid wall mesh, so the depth buffer and the
 * existing back-face handling also apply to tall, translucent and concave booths. */
function wallCorners(
  target: number[],
  points: readonly VenueMapWebGLPoint[],
  height: number,
  color: Color,
) {
  const winding = Math.sign(area(points));
  const cornerIndices = new Set(mapPolygonCornerIndices(points));
  points.forEach((point, index) => {
    const nextIndex = (index + 1) % points.length;
    const next = points[nextIndex];
    const length = Math.hypot(next.x - point.x, next.y - point.y);
    if (!length) return;
    const dx = (next.x - point.x) / length;
    const dy = (next.y - point.y) / length;
    const normal = [winding * dy, -winding * dx, 0];
    const width = Math.min(0.325, length / 2);
    // A small outward offset prevents coplanar flicker without detaching the
    // edge from its wall. Only source-outlined booths allocate these strips.
    const offsetX = normal[0] * 0.012;
    const offsetY = normal[1] * 0.012;
    for (const [from, to] of [
      ...(cornerIndices.has(index) ? [[0, width]] : []),
      ...(cornerIndices.has(nextIndex) ? [[length - width, length]] : []),
    ]) {
      const a = {
        x: point.x + dx * from + offsetX,
        y: point.y + dy * from + offsetY,
      };
      const b = {
        x: point.x + dx * to + offsetX,
        y: point.y + dy * to + offsetY,
      };
      const corners = [
        { ...a, z: 0 },
        { ...b, z: 0 },
        { ...b, z: height },
        { ...a, z: height },
      ];
      for (const corner of [0, 1, 2, 0, 2, 3])
        vertex(target, corners[corner], normal, color);
    }
  });
}

function border(
  target: number[],
  points: readonly VenueMapWebGLPoint[],
  z: number,
  color: Color,
) {
  points.forEach((point, index) => {
    const next = points[(index + 1) % points.length];
    const length = Math.hypot(next.x - point.x, next.y - point.y);
    if (!length) return;
    const dx = ((next.y - point.y) / length) * 0.22;
    const dy = (-(next.x - point.x) / length) * 0.22;
    surface(
      target,
      [
        { x: point.x + dx, y: point.y + dy },
        { x: next.x + dx, y: next.y + dy },
        { x: next.x - dx, y: next.y - dy },
        { x: point.x - dx, y: point.y - dy },
      ],
      z,
      color,
    );
  });
}

function floorOutline(geometry: MapGeometry) {
  const f = geometry.floor;
  return [
    { x: f.x, y: f.y },
    { x: f.x + f.width, y: f.y },
    { x: f.x + f.width, y: f.y + f.height },
    { x: f.x, y: f.y + f.height },
  ];
}

const architectureMeshes = new WeakMap<
  NonNullable<MapGeometry["architecture"]>,
  Float32Array
>();

function architectureMesh(architecture: MapGeometry["architecture"]) {
  if (!architecture?.length) return undefined;
  const cached = architectureMeshes.get(architecture);
  if (cached) return cached;
  const color = venueMapWebGLColor(VENUE_MAP_ARCHITECTURE_COLOR);
  let vertexCount = 0;
  const polygons = architecture.map((polygon) => {
    const { vertices: coordinates, holes } = flatten(polygon);
    const indices = earcut(coordinates, holes, 2);
    vertexCount += indices.length;
    return { coordinates, indices };
  });
  const mesh = new Float32Array(vertexCount * 10);
  let offset = 0;
  for (const { coordinates, indices } of polygons) {
    for (const index of indices) {
      mesh[offset++] = coordinates[index * 2];
      mesh[offset++] = coordinates[index * 2 + 1];
      mesh[offset++] = VENUE_MAP_ARCHITECTURE_HEIGHT;
      mesh[offset++] = 0;
      mesh[offset++] = 0;
      mesh[offset++] = 1;
      mesh.set(color, offset);
      offset += 4;
    }
  }
  // Route emphasis can derive new booth geometry without changing the floor.
  // Retain one triangulation per immutable outline, never per camera frame.
  architectureMeshes.set(architecture, mesh);
  return mesh;
}

const artworkMeshes = new WeakMap<
  NonNullable<MapGeometry["artwork"]>,
  Float32Array
>();

/** Ordered source paint: one immutable triangulation, independent of the camera. */
function artworkMesh(artwork: MapGeometry["artwork"]): Float32Array {
  if (!artwork?.length) return new Float32Array();
  const cached = artworkMeshes.get(artwork);
  if (cached) return cached;
  let vertexCount = 0;
  const polygons = artwork.flatMap((item) =>
    item.polygons.map((polygon) => {
      const { vertices: coordinates, holes } = flatten(polygon);
      const indices = earcut(coordinates, holes, 2);
      vertexCount += indices.length;
      return {
        coordinates,
        indices,
        color: venueMapWebGLColor(item.color, item.opacity ?? 1),
      };
    }),
  );
  const mesh = new Float32Array(vertexCount * 10);
  let offset = 0;
  for (const { coordinates, indices, color } of polygons) {
    for (const index of indices) {
      mesh[offset++] = coordinates[index * 2];
      mesh[offset++] = coordinates[index * 2 + 1];
      mesh[offset++] = VENUE_MAP_ARTWORK_HEIGHT;
      mesh[offset++] = 0;
      mesh[offset++] = 0;
      mesh[offset++] = 1;
      mesh.set(color, offset);
      offset += 4;
    }
  }
  artworkMeshes.set(artwork, mesh);
  return mesh;
}

export interface VenueMapVertexRange {
  start: number;
  end: number;
}

interface StandVertexRange extends VenueMapVertexRange {
  standId: string;
  height: number;
  roof: string;
  roofTint: number;
  borderColor: string | undefined;
  opacity: number;
  parts: {
    start: number;
    wallEnd: number;
    cornerEnd: number;
    roofEnd: number;
    end: number;
  }[];
}

// Immutable footprints are triangulated once. Appearance updates retain these
// offsets and only edit elevation/colour in the existing typed array and GPU buffer.
const standVertexRanges = new WeakMap<Float32Array, StandVertexRange[]>();

function tinted(base: Color, amount: number): Color {
  return [
    base[0] + (1 - base[0]) * amount,
    base[1] + (1 - base[1]) * amount,
    base[2] + (1 - base[2]) * amount,
    base[3],
  ];
}

export function updateVenueMapWebGLStandVertices(
  vertices: Float32Array,
  geometry: MapGeometry,
): VenueMapVertexRange[] {
  const ranges = standVertexRanges.get(vertices);
  if (!ranges) throw new Error("Unknown booth vertex buffer");
  const stands = new Map(geometry.stands.map((stand) => [stand.id, stand]));
  const changed: VenueMapVertexRange[] = [];
  for (const range of ranges) {
    const stand = stands.get(range.standId);
    if (!stand) continue;
    const height = mapStandHeight(stand);
    const roof = mapStandColors(stand).roof;
    const opacity = mapPreviewOpacity(stand);
    const borderColor = mapStandBorderColor(stand);
    if (
      range.height === height &&
      range.roof === roof &&
      range.roofTint === mapStandRoofTint(stand) &&
      range.borderColor === borderColor &&
      range.opacity === opacity
    )
      continue;
    const base = venueMapWebGLColor(roof, opacity);
    const roofTint = mapStandRoofTint(stand);
    const roofColor = tinted(base, roofTint);
    const rimColor = borderColor
      ? venueMapWebGLColor(borderColor, opacity)
      : tinted(base, VENUE_MAP_RIM_TINT);
    const roofHeight = Math.max(0.025, height);
    for (const part of range.parts) {
      for (let offset = part.start; offset < part.end; offset += 10) {
        const wall = offset < part.wallEnd;
        const cornerInk = !wall && offset < part.cornerEnd;
        if (range.height !== height) {
          // Each wall quad is [bottom,bottom,top, bottom,top,top]. Keep
          // zero-height walls degenerate so lifting a flat booth needs no rebuild.
          const corner =
            ((offset - (cornerInk ? part.wallEnd : part.start)) / 10) % 6;
          vertices[offset + 2] =
            wall || cornerInk
              ? corner === 2 || corner >= 4
                ? height
                : 0
              : roofHeight + (offset < part.roofEnd ? 0 : 0.02);
        }
        if (
          range.roof !== roof ||
          range.roofTint !== roofTint ||
          range.borderColor !== borderColor ||
          range.opacity !== opacity
        )
          vertices.set(
            wall
              ? base
              : cornerInk
                ? borderColor
                  ? rimColor
                  : base
                : offset < part.roofEnd
                  ? roofColor
                  : rimColor,
            offset + 6,
          );
      }
    }
    range.height = height;
    range.roof = roof;
    range.roofTint = roofTint;
    range.borderColor = borderColor;
    range.opacity = opacity;
    changed.push({ start: range.start, end: range.end });
  }
  return changed;
}

export interface VenueMapWebGLStandDrawRange {
  first: number;
  count: number;
  center: readonly [number, number, number];
}

/** Keep opaque booths in a single batch where possible; only editor ghosts need ordering. */
export function venueMapWebGLStandDrawRanges(vertices: Float32Array): {
  opaque: VenueMapWebGLStandDrawRange[];
  translucent: VenueMapWebGLStandDrawRange[];
} {
  const ranges = standVertexRanges.get(vertices);
  if (!ranges) throw new Error("Unknown booth vertex buffer");
  const opaque: VenueMapWebGLStandDrawRange[] = [];
  const translucent: VenueMapWebGLStandDrawRange[] = [];
  for (const range of ranges) {
    if (range.start === range.end) continue;
    const first = range.start / 10;
    const count = (range.end - range.start) / 10;
    if (range.opacity === 1) {
      const previous = opaque[opaque.length - 1];
      if (previous && previous.first + previous.count === first)
        previous.count += count;
      else opaque.push({ first, count, center: [0, 0, 0] });
      continue;
    }
    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;
    for (let offset = range.start; offset < range.end; offset += 10) {
      minX = Math.min(minX, vertices[offset]);
      maxX = Math.max(maxX, vertices[offset]);
      minY = Math.min(minY, vertices[offset + 1]);
      maxY = Math.max(maxY, vertices[offset + 1]);
    }
    translucent.push({
      first,
      count,
      center: [(minX + maxX) / 2, (minY + maxY) / 2, range.height / 2],
    });
  }
  return { opaque, translucent };
}

export function buildVenueMapWebGLMeshes(geometry: MapGeometry) {
  const floor: number[] = [];
  const stands: number[] = [];
  const floorPoints = floorOutline(geometry);
  walls(floor, floorPoints, -5, 0, venueMapWebGLColor("#d6d9d1"));
  surface(
    floor,
    floorPoints,
    0,
    venueMapWebGLColor(geometry.floor.color ?? "#faf8f2"),
  );
  border(floor, floorPoints, 0.015, venueMapWebGLColor("#d1d7ce"));
  // Dots follow the floor's perspective and depth, while their lettering stays
  // upright in the entrance atlas. Reuse the existing floor buffer and draw call.
  for (const entrance of geometry.entrances) {
    surface(
      floor,
      mapEntranceDotPoints(entrance),
      VENUE_MAP_ENTRANCE_DOT_HEIGHT,
      venueMapWebGLColor(
        VENUE_MAP_ENTRANCE_DOT_COLOR,
        mapPreviewOpacity(entrance),
      ),
    );
  }
  // Reuse the floor draw call and buffer; holes preserve open doorways and rooms.
  // Copy typed data directly rather than creating a second large JS number array.
  const architecture = architectureMesh(geometry.architecture);
  const floorMesh = new Float32Array(
    floor.length + (architecture?.length || 0),
  );
  floorMesh.set(floor);
  if (architecture) floorMesh.set(architecture, floor.length);
  const ranges: StandVertexRange[] = [];
  for (const stand of geometry.stands) {
    if (stand.hidden) continue;
    const height = mapStandHeight(stand);
    // A flat booth still needs its own visible surface above the floor mesh.
    const roofHeight = Math.max(0.025, height);
    const palette = mapStandColors(stand);
    const opacity = mapPreviewOpacity(stand);
    const base = venueMapWebGLColor(palette.roof, opacity);
    const range: StandVertexRange = {
      standId: stand.id,
      start: stands.length,
      end: stands.length,
      height,
      roof: palette.roof,
      roofTint: mapStandRoofTint(stand),
      borderColor: mapStandBorderColor(stand),
      opacity,
      parts: [],
    };
    for (const polygon of stand.polygons) {
      const points = mapPolygonBoundary(polygon.map(([x, y]) => ({ x, y })));
      const start = stands.length;
      walls(stands, points, 0, height, base);
      const wallEnd = stands.length;
      if (range.borderColor)
        wallCorners(
          stands,
          points,
          height,
          venueMapWebGLColor(range.borderColor, opacity),
        );
      const cornerEnd = stands.length;
      surface(
        stands,
        points,
        roofHeight,
        tinted(base, mapStandRoofTint(stand)),
      );
      const roofEnd = stands.length;
      border(
        stands,
        points,
        roofHeight + 0.02,
        range.borderColor
          ? venueMapWebGLColor(range.borderColor, opacity)
          : tinted(base, VENUE_MAP_RIM_TINT),
      );
      range.parts.push({
        start,
        wallEnd,
        cornerEnd,
        roofEnd,
        end: stands.length,
      });
    }
    range.end = stands.length;
    ranges.push(range);
  }
  const standMesh = new Float32Array(stands);
  standVertexRanges.set(standMesh, ranges);
  return {
    floor: floorMesh,
    artwork: artworkMesh(geometry.artwork),
    stands: standMesh,
  };
}
